import { useAddToWallet, useWallet } from '../lib/queries';
import { useI18n, type StringKey } from '../lib/i18n';
import { Card } from './ui';

const APPLE_MARK = (
  <svg viewBox="0 0 24 24" className="size-5 shrink-0" fill="currentColor" aria-hidden="true">
    <path d="M17.05 12.53c-.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.61-1.7-3.18-1.73-1.35-.14-2.64.8-3.33.8-.69 0-1.75-.78-2.87-.76-1.48.02-2.84.86-3.6 2.18-1.53 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.25 2.74 2.2 1.1-.04 1.51-.71 2.84-.71 1.32 0 1.7.71 2.86.69 1.18-.02 1.93-1.08 2.65-2.14.84-1.23 1.18-2.42 1.2-2.48-.03-.01-2.29-.88-2.31-3.5zM14.9 5.9c.6-.74 1.01-1.76.9-2.78-.87.04-1.93.58-2.56 1.31-.56.65-1.05 1.69-.92 2.69.97.07 1.96-.49 2.58-1.22z" />
  </svg>
);

/** Neutral, and deliberately not a Google mark: redrawing theirs is forbidden. */
const PHONE_MARK = (
  <svg viewBox="0 0 24 24" className="size-5 shrink-0" fill="none" aria-hidden="true">
    <rect x="6" y="2.5" width="12" height="19" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M10.5 18.5h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/**
 * Google ships this button and forbids anyone redrawing it: the shape, the
 * colour, the font and the padding are theirs, in black only, at 48dp or
 * taller with 8dp of clear space. So it is their file, served as-is, in the
 * language the customer is reading.
 */
const GOOGLE_BUTTON: Record<string, string> = {
  ka: '/brand/wallet/add-to-google-wallet-ka.svg',
  ru: '/brand/wallet/add-to-google-wallet-ru.svg',
  en: '/brand/wallet/add-to-google-wallet-en.svg',
};

export function WalletButtons() {
  const { t, language } = useI18n();
  const { data } = useWallet();
  const add = useAddToWallet();

  if (!data) return null;
  const { apple, google } = data.available;
  if (!apple && !google) return null;

  const stateOf = (provider: 'APPLE' | 'GOOGLE') => {
    const pass = data.passes.find((entry) => entry.provider === provider);
    if (!pass) return 'none' as const;
    return pass.ready ? ('ready' as const) : ('pending' as const);
  };

  const saveUrlOf = (provider: 'APPLE' | 'GOOGLE') =>
    data.passes.find((entry) => entry.provider === provider)?.saveUrl ?? null;

  const shell = 'flex w-full items-center justify-center gap-2.5 rounded-xl border px-4 py-3 text-sm font-medium transition-colors';
  const plain = `${shell} border-line bg-raised text-ink hover:border-brand/50 disabled:cursor-default disabled:opacity-55 disabled:hover:border-line`;

  /**
   * Our own button, for our own step.
   *
   * Preparing the card is a request to us, not a Google flow, and Google's
   * button may only launch a Google flow — so this one is plainly ours and
   * says what it does. Their button appears once there is a card to save.
   */
  const ourButton = (provider: 'APPLE' | 'GOOGLE', mark: typeof APPLE_MARK, labelKey: StringKey) => {
    const state = stateOf(provider);
    const busy = add.isPending && add.variables === provider;

    return (
      <button
        key={provider}
        type="button"
        disabled={state !== 'none' || busy}
        onClick={() => add.mutate(provider)}
        className={plain}
      >
        {mark}
        <span>
          {state === 'ready' ? t('wallet.added')
            : state === 'pending' || busy ? t('wallet.preparing')
            : t(labelKey)}
        </span>
      </button>
    );
  };

  const googleReady = stateOf('GOOGLE') === 'ready' && saveUrlOf('GOOGLE');

  return (
    <Card>
      <h2 className="mb-1 text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-ink-faint">
        {t('wallet.title')}
      </h2>
      <p className="mb-3 text-xs leading-relaxed text-ink-dim">{t('wallet.hint')}</p>
      <div className="space-y-2">
        {apple && ourButton('APPLE', APPLE_MARK, 'wallet.apple')}

        {google && (googleReady ? (
          // p-2 is the 8dp of clear space Google asks for around the button.
          <a
            href={saveUrlOf('GOOGLE')!}
            target="_blank"
            rel="noreferrer"
            className="flex justify-center p-2"
            aria-label={t('wallet.google')}
          >
            <img
              src={GOOGLE_BUTTON[language] ?? GOOGLE_BUTTON['en']!}
              alt={t('wallet.google')}
              className="h-[50px] w-auto max-w-full"
            />
          </a>
        ) : ourButton('GOOGLE', PHONE_MARK, 'wallet.prepare'))}
      </div>
    </Card>
  );
}
